import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./style.module.scss";
import { useNavigate } from "react-router-dom";
import api from "@utils/api";

const theme = createTheme({
  palette: {
    primary: {
      main: "#ffffff", // White color for primary elements
    },
    text: {
      primary: "#ffffff", // White color for text
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-input": {
            color: "white",
          },
          "& .MuiInputLabel-root": {
            color: "white",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "white",
            },
            "&:hover fieldset": {
              borderColor: "white",
            },
            "&.Mui-focused fieldset": {
              borderColor: "white",
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "white",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "white",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: "white",
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "white",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "white",
        },
      },
    },
  },
});

const InterestForm = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const [formAnswers, setFormAnswers] = useState({
    birthDate: "",
    gender: "",
    comicsFrequency: "",
    format: "",
    genres: [],
    comicInspiration: "",
    appealing: [],
    analyze: "",
    fanContent: "",
    cosplay: "",
    conventions: "",
    ethnicity: "",
    education: "",
    employment: "",
    income: "",
    region: "",
    newsLetter: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      // Handle checkbox groups (e.g., genres, appealing)
      setFormAnswers((prevAnswers) => {
        const newSelection = checked
          ? [...prevAnswers[name], value]
          : prevAnswers[name].filter((item) => item !== value);
        return {
          ...prevAnswers,
          [name]: newSelection,
        };
      });
    } else {
      setFormAnswers((prevAnswers) => ({
        ...prevAnswers,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all questions are answered
    const requiredFields = [
      "birthDate",
      "gender",
      "comicsFrequency",
      "format",
      "genres",
      "comicInspiration",
      "appealing",
      "analyze",
      "fanContent",
      "cosplay",
      "conventions",
      "ethnicity",
      "education",
      "employment",
      "income",
      "region",
      "newsLetter",
    ];

    const missingFields = requiredFields.filter(
      (field) =>
        !formAnswers[field] ||
        (Array.isArray(formAnswers[field]) && formAnswers[field].length === 0)
    );

    if (missingFields.length > 0) {
      console.log(missingFields);
      console.log(formAnswers);
      toast.error("Please answer all questions before submitting!");
    } else {
      // Proceed with form submission
      toast.success("Form submitted successfully!");
      // Add your form submission logic here
      console.log("Form Answers:", formAnswers);
      api
        .post(
          "/register-form",
          {
            date_of_birth: formAnswers.birthDate,
            gender: formAnswers.gender,
            how_often_read_comics: formAnswers.comicsFrequency,
            format_preference: formAnswers.format,
            genres: formAnswers.genres.join(", "),
            what_got_you_into_comics: formAnswers.comicInspiration,
            what_appeals_to_you_most_about_comics:
              formAnswers.appealing.join(", "),
            how_do_you_prefer_to_discuss_a_series: formAnswers.analyze,
            do_you_create_any_fan_content: formAnswers.fanContent,
            do_you_cosplay: formAnswers.cosplay,
            do_you_attend_conventions: formAnswers.conventions,
            ethnicity: formAnswers.ethnicity,
            education: formAnswers.education,
            employment_status: formAnswers.employment,
            household_income: formAnswers.income,
            region: formAnswers.region,
            exclusive_offers_enable: formAnswers.newsLetter,
          },
          { headers }
        )
        .then((res) => {
          navigate("/thank-you");
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <ToastContainer />
      <Box
        sx={{
          color: "white",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "black",
        }}
      >
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <Typography
            variant="h4"
            gutterBottom
            className="font-bold"
            sx={{ fontWeight: "700" }}
          >
            Let's get to know each other! Complete your user profile and get an
            additional 10 free credits.
          </Typography>
        </div>
        <Container>
          <form onSubmit={handleSubmit}>
            {/* 1. Date of Birth */}
            <div className={styles.question_container}>
              <FormControl>
                <Typography
                  variant="h5"
                  gutterBottom
                  className="font-bold"
                  sx={{ fontWeight: "700" }}
                >
                  1. Please enter your date of birth
                </Typography>
                <TextField
                  name="birthDate"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  value={formAnswers.birthDate}
                  required
                />
              </FormControl>
            </div>

            {/* 2. Gender */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <FormLabel>
                  <Typography
                    variant="h5"
                    gutterBottom
                    className="font-bold"
                    sx={{ fontWeight: "700" }}
                  >
                    2. What do you identify as?
                  </Typography>
                </FormLabel>
                <RadioGroup
                  name="gender"
                  value={formAnswers.gender}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    label="Male (He/him)"
                  />
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    label="Female (She/her)"
                  />
                  <FormControlLabel
                    value="non-binary"
                    control={<Radio />}
                    label="Non-binary (They/Them)"
                  />
                  <FormControlLabel
                    value="prefer-not-to-say"
                    control={<Radio />}
                    label="Prefer not to say"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 3. Reading Frequency */}
            <div className={styles.question_container}>
              <FormControl fullWidth margin="normal">
                <Typography
                  variant="h5"
                  gutterBottom
                  className="font-bold"
                  sx={{ fontWeight: "700" }}
                >
                  3. How often do you read comics/manga?
                </Typography>
                <RadioGroup
                  name="comicsFrequency"
                  value={formAnswers.comicsFrequency}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="daily"
                    control={<Radio />}
                    label="Daily"
                  />
                  <FormControlLabel
                    value="weekly"
                    control={<Radio />}
                    label="Weekly"
                  />
                  <FormControlLabel
                    value="monthly"
                    control={<Radio />}
                    label="Monthly"
                  />
                  <FormControlLabel
                    value="occasionally"
                    control={<Radio />}
                    label="Occasionally"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 4. Reading Format */}
            <div className={styles.question_container}>
              <FormControl fullWidth margin="normal">
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  4. Which format do you prefer reading?
                </Typography>
                <RadioGroup
                  name="format"
                  value={formAnswers.format}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="print"
                    control={<Radio />}
                    label="Print (physical copies)"
                  />
                  <FormControlLabel
                    value="digital"
                    control={<Radio />}
                    label="Digital (online/app)"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 5. Genres */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  5. What genres do you enjoy reading/watching? (Select all that
                  apply)
                </Typography>
                <FormGroup>
                  {[
                    "Action",
                    "Adventure",
                    "Comedy",
                    "Drama",
                    "Ecchi",
                    "Fantasy",
                    "Horror",
                    "Isekai",
                    "Mystery",
                    "Romance",
                    "Sci-Fi",
                    "Senien",
                    "Shonen",
                    "Slice of Life",
                    "Thriller",
                    "Yaoi",
                    "Yuri",
                  ].map((genre) => (
                    <FormControlLabel
                      key={genre}
                      name="genres"
                      value={genre}
                      checked={formAnswers.genres.includes(genre)}
                      onChange={handleChange}
                      control={
                        <Checkbox

                        />
                      }
                      label={genre}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </div>

            {/* 6. Comic Inspiration */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  6. What got you into anime/manga/comics?
                </Typography>
                <RadioGroup
                  name="comicInspiration"
                  value={formAnswers.comicInspiration}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="friends"
                    control={<Radio />}
                    label="Friends or Family"
                  />
                  <FormControlLabel
                    value="movies-tv"
                    control={<Radio />}
                    label="Movies/TV Shows"
                  />
                  <FormControlLabel
                    value="social-media"
                    control={<Radio />}
                    label="Social Media"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 7. Appealing Features */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  7. What appeals to you most about anime/manga/comics? (Select
                  all that apply)
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="appealing"
                        value="storytelling"
                        checked={formAnswers.appealing.includes("storytelling")}
                        onChange={handleChange}
                      />
                    }
                    label="Storytelling"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="appealing"
                        value="artwork"
                        checked={formAnswers.appealing.includes("artwork")}
                        onChange={handleChange}
                      />
                    }
                    label="Artwork"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="appealing"
                        value="characters"
                        checked={formAnswers.appealing.includes("characters")}
                        onChange={handleChange}
                      />
                    }
                    label="Characters"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="appealing"
                        value="escapism"
                        checked={formAnswers.appealing.includes("escapism")}
                        onChange={handleChange}
                      />
                    }
                    label="Escapism"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="appealing"
                        value="community"
                        checked={formAnswers.appealing.includes("community")}
                        onChange={handleChange}
                      />
                    }
                    label="Community"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="appealing"
                        value="nostalgia"
                        checked={formAnswers.appealing.includes("nostalgia")}
                        onChange={handleChange}
                      />
                    }
                    label="Nostalgia"
                  />
                </FormGroup>
              </FormControl>
            </div>

            {/* 8. Analyze Content */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  8. How do you prefer to discuss/analyze a series?
                </Typography>
                <RadioGroup
                  name="analyze"
                  value={formAnswers.analyze}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="online-fan-forums"
                    control={<Radio />}
                    label="Online fan forums"
                  />
                  <FormControlLabel
                    value="reviews-and-analysis-videos"
                    control={<Radio />}
                    label="Reviews and analysis videos"
                  />
                  <FormControlLabel
                    value="keep-thoughts-to-myself"
                    control={<Radio />}
                    label="I keep my thoughts to myself"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other (please specify)"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 9. Fan Content */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  9. Do you create any fan content? (Fan art, cosplay, fan
                  fiction etc.)
                </Typography>
                <RadioGroup
                  name="fanContent"
                  value={formAnswers.fanContent}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="create"
                    control={<Radio />}
                    label="Hell Yes! I create."
                  />
                  <FormControlLabel
                    value="enjoy"
                    control={<Radio />}
                    label="I don't create but enjoy fan content"
                  />
                  <FormControlLabel
                    value="not-interested"
                    control={<Radio />}
                    label="Not interested in fan content"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 10. Cosplay */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  10. Do you cosplay?
                </Typography>
                <RadioGroup
                  name="cosplay"
                  value={formAnswers.cosplay}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="yes-frequently"
                    control={<Radio />}
                    label="Yes, frequently"
                  />
                  <FormControlLabel
                    value="yes-occasionally"
                    control={<Radio />}
                    label="Yes, occasionally"
                  />
                  <FormControlLabel
                    value="no-but-interested"
                    control={<Radio />}
                    label="No, but interested in trying"
                  />
                  <FormControlLabel
                    value="not-interested"
                    control={<Radio />}
                    label="Not interested"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 11. Conventions */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  11. Do you attend conventions?
                </Typography>
                <RadioGroup
                  name="conventions"
                  value={formAnswers.conventions}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="yes-frequently"
                    control={<Radio />}
                    label="Yes, frequently"
                  />
                  <FormControlLabel
                    value="yes-occasionally"
                    control={<Radio />}
                    label="Yes, occasionally"
                  />
                  <FormControlLabel
                    value="no-but-interested"
                    control={<Radio />}
                    label="No, but interested"
                  />
                  <FormControlLabel
                    value="not-interested"
                    control={<Radio />}
                    label="Not interested"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 12. Ethnicity */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  12. What is your ethnicity?
                </Typography>
                <RadioGroup
                  name="ethnicity"
                  value={formAnswers.ethnicity}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="caucasian"
                    control={<Radio />}
                    label="Caucasian"
                  />
                  <FormControlLabel
                    value="african-american"
                    control={<Radio />}
                    label="African American"
                  />
                  <FormControlLabel
                    value="asian"
                    control={<Radio />}
                    label="Asian"
                  />
                  <FormControlLabel
                    value="hispanic-latino"
                    control={<Radio />}
                    label="Hispanic/Latino"
                  />
                  <FormControlLabel
                    value="native-american"
                    control={<Radio />}
                    label="Native American"
                  />
                  <FormControlLabel
                    value="pacific-islander"
                    control={<Radio />}
                    label="Pacific Islander"
                  />
                  <FormControlLabel
                    value="bi-racial"
                    control={<Radio />}
                    label="Bi-Racial"
                  />
                  <FormControlLabel
                    value="prefer-not-to-say"
                    control={<Radio />}
                    label="Prefer not to say"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 13. Education */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  13. What is the highest level of education you have completed?
                </Typography>
                <RadioGroup
                  name="education"
                  value={formAnswers.education}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="some-high-school"
                    control={<Radio />}
                    label="Some high school"
                  />
                  <FormControlLabel
                    value="high-school-diploma"
                    control={<Radio />}
                    label="High school diploma/GED"
                  />
                  <FormControlLabel
                    value="some-college"
                    control={<Radio />}
                    label="Some college"
                  />
                  <FormControlLabel
                    value="associates-degree"
                    control={<Radio />}
                    label="Associate's degree"
                  />
                  <FormControlLabel
                    value="bachelors-degree"
                    control={<Radio />}
                    label="Bachelor's degree"
                  />
                  <FormControlLabel
                    value="masters-degree"
                    control={<Radio />}
                    label="Master's degree"
                  />
                  <FormControlLabel
                    value="doctoral-professional-degree"
                    control={<Radio />}
                    label="Doctoral/Professional Degree"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 14. Occupation */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  14. What is your current employment status?
                </Typography>
                <RadioGroup
                  name="employment"
                  value={formAnswers.employment}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="employed-full-time"
                    control={<Radio />}
                    label="Employed full-time"
                  />
                  <FormControlLabel
                    value="employed-part-time"
                    control={<Radio />}
                    label="Employed part-time"
                  />
                  <FormControlLabel
                    value="self-employed"
                    control={<Radio />}
                    label="Self-employed"
                  />
                  <FormControlLabel
                    value="student"
                    control={<Radio />}
                    label="Student"
                  />
                  <FormControlLabel
                    value="unemployed"
                    control={<Radio />}
                    label="Unemployed"
                  />
                  <FormControlLabel
                    value="retired"
                    control={<Radio />}
                    label="Retired"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* 15. Reading Format */}
            <div className={styles.question_container}>
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: "700" }}>
                  15. What is your approximate annual household income?
                </Typography>
                <RadioGroup
                  name="income"
                  value={formAnswers.income}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="<25k"
                    control={<Radio />}
                    label="Under $25,000"
                  />
                  <FormControlLabel
                    value="25k-50k"
                    control={<Radio />}
                    label="$25,000 - $50,000"
                  />
                  <FormControlLabel
                    value="50k-75k"
                    control={<Radio />}
                    label="$50,000 - $75,000"
                  />
                  <FormControlLabel
                    value="75k-100k"
                    control={<Radio />}
                    label="$75,000 - $100,000"
                  />
                  <FormControlLabel
                    value=">100k"
                    control={<Radio />}
                    label="Over $100,000"
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <div className={styles.question_container}>
              {" "}
              <FormControl>
                <Typography variant="h5" style={{ fontWeight: 700 }}>
                  16. Which region do you live in?
                </Typography>
                <RadioGroup
                  name="region"
                  value={formAnswers.region}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="northeast-us"
                    control={<Radio />}
                    label="Northeast US"
                  />
                  <FormControlLabel
                    value="southeast-us"
                    control={<Radio />}
                    label="Southeast US"
                  />
                  <FormControlLabel
                    value="midwest-us"
                    control={<Radio />}
                    label="Midwest US"
                  />
                  <FormControlLabel
                    value="west-us"
                    control={<Radio />}
                    label="West US"
                  />
                  <FormControlLabel
                    value="southwest-us"
                    control={<Radio />}
                    label="Southwest US"
                  />
                  <FormControlLabel
                    value="outside-us"
                    control={<Radio />}
                    label="Outside of US (Please Specify Country)"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* Newsletter Signup */}
            <div className={styles.question_container}>
              {" "}
              <FormControl fullWidth>
                <Typography variant="h5" style={{ fontWeight: 700 }}>
                  17. Be the first to get exclusive offers, free credits, and
                  anime/manga/comic updates delivered right to your inbox! (We
                  pinky promise never to spam you - just quality content and
                  special deals for our loyal users)
                </Typography>
                <RadioGroup
                  name="newsLetter"
                  value={formAnswers.newsLetter}
                  onChange={handleChange}
                  required
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* Submit Button */}
            <div className={styles.button_container}>
              <Button
                style={{ marginTop: "1rem", marginBottom: "1rem" }}
                variant="contained"
                color="primary"
                type="submit"
              >
                Submit
              </Button>
            </div>
          </form>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default InterestForm;
